import React from 'react';
import { RouteComponentProps } from 'react-router';
import Orders from 'pages/Orders/Orders';
import CouriersMap from 'pages/CouriersMap/CouriersMap';
import UserEdit from 'pages/UserEdit/UserEdit';
import Users from 'pages/Users/Users';
import ConfirmList from 'pages/ConfirmList/ConfirmList';
import ActivationList from 'pages/ActivationList/ActivationList';
import ConfirmedList from 'pages/ConfirmedList/ConfirmedList';
import CourierActivation from 'pages/CourierActivation/CourierActivation';
import Roles from 'pages/Roles/Roles';
import RoleAccesses from 'pages/RoleAccesses/RoleAccesses';
import Report from 'pages/Report/Report';
import Regions from 'pages/Regions/Regions';
import RegionDetails from 'pages/RegionDetails/RegionDetails';
import Branches from 'pages/Branches/Branches';
import Statuses from 'pages/Statuses/Statuses';
import Actions from 'pages/Actions/Actions';
import EmcTypes from 'pages/EmcTypes/EmcTypes';
import RejectReasons from 'pages/RejectReasons/RejectReasons';
import Profile from 'pages/Profile/Profile';
import Flights from 'pages/Flights/Flights';
import ResetPassword from 'pages/ResetPassword/ResetPassword';
import Dashboard from 'pages/Dashboard/Dashboard';
import FreelanceCouriers from 'pages/FreelanceCouriers/FreelanceCouriers';
import FreelanceCourierEdit from 'pages/FreelanceCourierEdit/FreelanceCourierEdit';
import { CourierRegType } from 'api/couriers';
import CourierConfirm from 'components/organisms/CourierConfirm/CourierConfirm';
import OrderDetailsDialog from 'components/organisms/OrderDetailsDialog/OrderDetailsDialog';
import { permissions } from '../constants';
import NPO from '../pages/NPO/NPO';
import PaymentDetails from '../pages/PaymentDetails/PaymentDetails';
import WorkingDays from '../pages/WorkingDays/WorkingDays';
import { PrivateRoute } from './PrivateRoute';
import Relog from 'pages/Relog/Relog';
import Pension from 'pages/Pension/Pension';

const Settings = React.lazy(() => import('pages/Settings/Settings'));
const BranchDetails = React.lazy(() => import('pages/BranchDetails/BranchDetails'));

export interface RouteItem {
    name: string;
    icon?: string;
    path?: string;
    component?: React.ComponentType<RouteComponentProps<any>> | React.ComponentType<any>;
    permissions: permissions[];
    hidden?: boolean;
    items?: Omit<RouteItem, 'icon'>[];
}

export enum RouteNames {
    RELOG = 'Relog',
}

export const routeList: RouteItem[] = [
    {
        name: 'NewCouriers',
        icon: 'GroupAdd',
        permissions: [permissions.Admin, permissions.HR],
        items: [
            {
                path: '/cabinet/courier-confirmations',
                name: 'Confirmations',
                component: ConfirmList,
                permissions: [permissions.Admin, permissions.HR],
                items: [
                    {
                        name: 'CourierConfirm',
                        path: '/cabinet/courier-confirm/:id',
                        permissions: [permissions.Admin, permissions.HR],
                        component: () => <CourierConfirm type={CourierRegType.New} />,
                        hidden: true,
                    },
                ],
            },
            {
                path: '/cabinet/courier-confirmed-list',
                component: ConfirmedList,
                name: 'Confirmed',
                permissions: [permissions.Admin, permissions.HR],
                items: [
                    {
                        name: 'CourierConfirmed',
                        path: '/cabinet/courier-confirmed/:id',
                        component: () => <CourierConfirm type={CourierRegType.Confirmed} />,
                        permissions: [permissions.Admin, permissions.HR],
                        hidden: true,
                    },
                ],
            },
            {
                path: '/cabinet/courier-activations',
                component: ActivationList,
                name: 'Activations',
                permissions: [permissions.Admin, permissions.HR],
                items: [
                    {
                        name: 'CourierActivation',
                        path: '/cabinet/courier-activation/:id',
                        component: CourierActivation,
                        permissions: [permissions.Admin, permissions.HR],
                        hidden: true,
                    },
                ],
            },
        ],
        hidden: true,
    },
    {
        name: 'SituationCenter',
        icon: 'DonutSmall',
        permissions: [
            permissions.Admin,
            permissions.Operator,
            permissions.PensionOperator,
            permissions.Dispatcher,
            permissions.Coordinator,
        ],
        items: [
            {
                path: '/cabinet/couriers-map',
                component: CouriersMap,
                name: 'CouriersMap',
                permissions: [
                    permissions.Admin,
                    permissions.Operator,
                    permissions.PensionOperator,
                    permissions.Dispatcher,
                    permissions.Coordinator,
                ],
            },
        ],
    },
    {
        name: RouteNames.RELOG,
        icon: 'PlacePurple',
        path: '/cabinet/relog',
        component: Relog,
        permissions: [
            permissions.Admin,
            permissions.Operator,
            permissions.Dispatcher,
            permissions.Coordinator,
            permissions.Support,
        ],
    },
    {
        name: 'AllRequests',
        icon: 'BorderAll',
        path: '/cabinet/all-requests',
        component: Orders,
        permissions: [
            permissions.Admin,
            permissions.Operator,
            permissions.PensionOperator,
            permissions.Dispatcher,
            permissions.Coordinator,
            permissions.Support,
        ],
    },
    {
        name: 'Flights',
        icon: 'LocalShipping',
        path: '/cabinet/flights',
        component: Flights,
        permissions: [
            permissions.Admin,
            permissions.Operator,
            permissions.PensionOperator,
            permissions.Coordinator,
            permissions.Support,
        ],
    },
    {
        name: 'OrdersByStatuses',
        icon: 'LocalPostOffice',
        permissions: [
            permissions.Admin,
            permissions.Operator,
            permissions.PensionOperator,
            permissions.Dispatcher,
        ],
        items: [
            {
                name: 'OrdersByStatusesItem',
                path: '/cabinet/requests/:status?',
                component: Orders,
                permissions: [
                    permissions.Admin,
                    permissions.Operator,
                    permissions.PensionOperator,
                    permissions.Dispatcher,
                ],
                hidden: true,
                items: [
                    {
                        name: 'OrderDetails',
                        path: '/cabinet/request-details/:id?',
                        component: OrderDetailsDialog,
                        permissions: [
                            permissions.Admin,
                            permissions.Operator,
                            permissions.PensionOperator,
                            permissions.Dispatcher,
                        ],
                        hidden: true,
                    },
                ],
            },
        ],
    },
     {
         name: 'Reports',
         path: '/reports',
         permissions: [
             permissions.Admin,
             permissions.Operator,
             permissions.PensionOperator,
             permissions.Dispatcher,
             permissions.HR,
             permissions.Coordinator,
         ],
         component: Report,
         icon: 'Assessment',
     },
    {
        name: 'UsersAndRoles',
        icon: 'PeopleIcon',
        permissions: [
            permissions.Admin,
            //permissions.Coordinator,
            permissions.HR,
            permissions.Support,
        ],
        items: [
            {
                name: 'Users',
                path: '/cabinet/users',
                component: Users,
                permissions: [permissions.Admin, permissions.Coordinator, permissions.Support],
                items: [
                    {
                        name: 'UserEdit',
                        path: '/cabinet/user-edit/:id?',
                        component: UserEdit,
                        permissions: [
                            permissions.Admin,
                            permissions.Coordinator,
                            permissions.Support,
                        ],
                        hidden: true,
                    },
                ],
            },
            {
                name: 'FreelanceCouriers',
                path: '/cabinet/freelance-couriers',
                component: FreelanceCouriers,
                permissions: [permissions.Admin, permissions.HR, permissions.Coordinator],
                items: [
                    {
                        name: 'FreelanceCourierEdit',
                        path: '/cabinet/freelance-courier/:id/details',
                        component: FreelanceCourierEdit,
                        permissions: [permissions.Admin, permissions.HR, permissions.Coordinator],
                        hidden: true,
                    },
                ],
                hidden: true,
            },
            {
                name: 'Roles',
                component: Roles,
                path: '/cabinet/roles',
                permissions: [permissions.Admin],
            },
            {
                name: 'AccessRights',
                path: '/cabinet/accesses',
                component: RoleAccesses,
                permissions: [permissions.Admin],
            },
        ],
    },
    {
        name: 'Dictionaries',
        permissions: [permissions.Admin, permissions.Support],
        icon: 'ListAlt',
        items: [
            {
                name: 'Regions',
                component: Regions,
                path: '/cabinet/regions',
                permissions: [permissions.Admin],
                items: [
                    {
                        name: 'RegionDetails',
                        path: '/cabinet/region-details/:id?',
                        component: RegionDetails,
                        permissions: [permissions.Admin],
                        hidden: true,
                    },
                ],
            },
            {
                name: 'Branches',
                component: Branches,
                path: '/cabinet/branches',
                permissions: [permissions.Admin, permissions.Support],
                items: [
                    {
                        name: 'BranchDetails',
                        path: '/cabinet/branch-details/:id?',
                        component: BranchDetails,
                        permissions: [permissions.Admin, permissions.Support],
                        hidden: true,
                    },
                ],
            },
            {
                name: 'Statuses',
                component: Statuses,
                path: '/cabinet/statuses',
                permissions: [permissions.Admin],
            },
            {
                name: 'Actions',
                component: Actions,
                path: '/cabinet/actions',
                permissions: [permissions.Admin],
            },
            {
                name: 'EmcTypes',
                component: EmcTypes,
                path: '/cabinet/emc-types',
                permissions: [permissions.Admin],
            },
            {
                name: 'RejectReasons',
                component: RejectReasons,
                path: '/cabinet/reject-reasons',
                permissions: [permissions.Admin],
            },
            {
                name: 'WorkingDays',
                component: WorkingDays,
                path: '/cabinet/working-days',
                permissions: [permissions.Admin],
            },
        ],
    },
    {
        name: 'NPO',
        icon: 'ImportContacts',
        component: NPO,
        path: '/cabinet/npo',
        permissions: [permissions.Admin, permissions.Operator, permissions.PensionOperator,],
    },
    {
        name: 'PaymentDetails',
        icon: 'Receipt',
        component: PaymentDetails,
        path: '/cabinet/payment-details',
        permissions: [permissions.Admin],
    },
    {
        name: 'SystemSettings',
        icon: 'Settings',
        component: Settings,
        path: '/cabinet/settings',
        permissions: [permissions.Admin],
    },
    {
        name: 'Profile',
        path: '/cabinet/profile',
        component: Profile,
        permissions: [
            permissions.Admin,
            permissions.Operator,
            permissions.PensionOperator,
            permissions.Dispatcher,
            permissions.HR,
            permissions.Coordinator,
            permissions.Support,
        ],
        hidden: true,
    },
    {
        name: 'ResetPassword',
        path: '/cabinet/reset-password',
        component: ResetPassword,
        permissions: [
            permissions.Admin,
            permissions.Operator,
            permissions.PensionOperator,
            permissions.Dispatcher,
            permissions.HR,
            permissions.Coordinator,
            permissions.Support,
        ],
        hidden: true,
    },
    {
        name: 'Cabinet',
        path: '/cabinet',
        component: Dashboard,
        hidden: true,
        permissions: [
            permissions.Admin,
            permissions.Operator,
            permissions.PensionOperator,
            permissions.Dispatcher,
            permissions.HR,
            permissions.Coordinator,
            permissions.Support,
        ],
    },
    {
        name: 'Kazpensia',
        icon: 'Security',
        path: '/pension',
        component: Pension,
        permissions: [
            permissions.Operator,
            permissions.Support,
        ],
    },
];

export function renderRouteList(list: RouteItem[], userPermissions: string[], relogEnabled: boolean): any {
    const result: any = [];

    list
        .filter(el => !(el.name === RouteNames.RELOG && !relogEnabled))
        .forEach((route) => {
            if (route.items) {
                result.push(renderRouteList(route.items, userPermissions, relogEnabled));
            }

            if (route.component && route.path) {
                result.push(
                    <PrivateRoute
                        key={route.path}
                        path={route.path}
                        userPermissions={userPermissions}
                        component={route.component}
                        permissions={route.permissions}
                    />
                );
            }
        });

    return result;
}
